import React from 'react'
// libs
import { useSnackbar } from 'notistack'
import { useMutation } from '@apollo/client/react'
// components
import AddMenu from './AddMenu'
import ActionButton from './ActionButton'
// constants
import { ENTITIES, Modals } from 'constants/index'
// context
import { LinkingModeContext } from 'containers/Plan/Edit/layers/API/Linking'
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
import { ObjectivesListAPIContext } from 'containers/ObjectivesList/layers/API'
// graphql
import { PLAN_OBJECTIVE_DELETE_MUTATION } from 'graphql/planObjectives/mutations'
import { PLAN_KPI_DELETE_MUTATION } from 'graphql/planKPIs/mutations'
import { PLAN_ACTION_DELETE_MUTATION } from 'graphql/planActions/mutations'
import { PLAN_ITEM_SET_ARCHIVED_MUTATION } from 'graphql/plan/mutations'
// hooks
import { usePermissions, useShowModal } from 'hooks/index'
// types
import { IPlanTypeLevelOption } from 'types/graphql'
import { ITableTreeNode } from 'components/UI/TableTree/types'
// views
import { AddLinkIcon, ArchiveIcon, ButtonsWrapper, LinkIcon, LinkOffIcon, TrashIcon, UndoIcon } from './views'
// utils
import get from 'lodash/get'
import { handleError } from 'utils/errors/handlers'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'

// Plan-item node (tree node + the plan-specific fields the actions use).
interface IPlanItemNode extends ITableTreeNode {
  entityId?: string
  planId?: string
  plan?: { locked?: boolean }
  linkItem?: () => void
  unlinkItem?: () => void
}

interface IProps {
  node: IPlanItemNode
}

const PlanItemActions: React.FC<IProps> = ({ node }) => {
  const { enableLinkingMode, isLinkingMode, linkingSource } = React.useContext(LinkingModeContext)
  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)
  const { deletePlanItem: removePlanItemFromPlan } = React.useContext(ObjectivesListAPIContext)

  const { showModal: showConfirmUnlinkModal } = useShowModal({ modalType: Modals.CONFIRM_UNLINK_PLAN_ITEM_MODAL })

  const [deleteKPIMutation] = useMutation(PLAN_KPI_DELETE_MUTATION)
  const [deleteActionMutation] = useMutation(PLAN_ACTION_DELETE_MUTATION)
  const [deleteObjectiveMutation] = useMutation(PLAN_OBJECTIVE_DELETE_MUTATION)
  const [archiveMutation] = useMutation(PLAN_ITEM_SET_ARCHIVED_MUTATION)

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_PLAN_ITEM })
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const isObjective = node.entityType === ENTITIES.PLAN_OBJECTIVE
  const isAction = node.entityType === ENTITIES.PLAN_ACTION
  const isKPI = node.entityType === ENTITIES.PLAN_KPI

  const {
    kpiAddKPI,
    kpiAddAction,
    kpiAddObjective,
    kpiLinkObjects,
    actionAddKPI,
    actionAddAction,
    actionAddObjective,
    actionLinkObjects,
    objectiveAddKPI,
    objectiveAddAction,
    objectiveAddObjective,
    objectiveLinkObjects,
    planLinkObjects,
    kpiDeleteKPI,
    actionDeleteAction,
    objectiveDeleteObjective,
    kpiArchiveKPI,
    actionArchiveAction,
    objectiveArchiveObjective,
    planAddItemsFromParentPlan
  } = usePermissions({ entity: node.entity, isAction, isKPI, isObjective })

  const shouldShowAddObjectiveButton =
    (isAction && actionAddObjective) || (isKPI && kpiAddObjective) || (isObjective && objectiveAddObjective)

  const shouldShowAddActionButton =
    (isAction && actionAddAction) || (isKPI && kpiAddAction) || (isObjective && objectiveAddAction)

  const shouldShowAddKPIButton = (isAction && actionAddKPI) || (isKPI && kpiAddKPI) || (isObjective && objectiveAddKPI)

  const canAdd = shouldShowAddObjectiveButton || shouldShowAddActionButton || shouldShowAddKPIButton

  const canLink =
    (isAction && actionLinkObjects) ||
    (isKPI && kpiLinkObjects) ||
    (isObjective && objectiveLinkObjects) ||
    planLinkObjects

  const canArchive =
    (isObjective && objectiveArchiveObjective) || (isAction && actionArchiveAction) || (isKPI && kpiArchiveKPI)

  const canDelete =
    (isObjective && objectiveDeleteObjective) || (isAction && actionDeleteAction) || (isKPI && kpiDeleteKPI)

  const shouldArchive = canArchive && !node.archived

  const levelOptions: IPlanTypeLevelOption[] = node.nextLevel?.typeOptions || []
  const nextLevelDescription = node.nextLevel?.description || 'item'

  const isLocked = node.plan?.locked

  // linking mode
  const linkingSourceKey = {
    [ENTITIES.PLAN_OBJECTIVE]: 'sourcePlanObjectiveId',
    [ENTITIES.PLAN_ACTION]: 'sourcePlanActionId',
    [ENTITIES.PLAN_KPI]: 'sourcePlanKPIId'
  }[linkingSource?.entityType as string]

  const isLinkingSource = isLinkingMode && linkingSource?.id === node.entityId

  const isNodeLinked =
    isLinkingMode &&
    (node.linkingItems?.destinationLinkingItems || []).some(
      (linkedItem) => linkedItem[linkingSourceKey] === linkingSource?.id
    )

  const shouldShowLinkButton = isLinkingMode && !isNodeLinked && !isLinkingSource && canLink

  // remove referenced item from sub plan
  const isNodeReferenced = !!node.referenced
  const isNodeHaveRealChildren = (node.children || []).some(({ referenced }) => !referenced)
  const shouldShowRemoveButton = isNodeReferenced && planAddItemsFromParentPlan

  const removeReferencedPlanItem = async () => {
    if (isNodeHaveRealChildren) {
      return enqueueSnackbar('Please remove subitems first!', {
        variant: 'warning',
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })
    }

    await removePlanItemFromPlan(node.id)
  }

  const linkPlanItem = () => {
    enableLinkingMode(node.entityId, node.planId, node.title, node.entityType)
  }

  const linkToSource = () => node.linkItem?.()

  const unlinkPlanItem = () => showConfirmUnlinkModal({ handleConfirm: node.unlinkItem, title: node.title })

  const archivePlanItem = () => {
    showModal({
      node,
      handleConfirm: async () => {
        try {
          await archiveMutation({ variables: { id: node.id, archived: true } })
          await treeQueryRefetch()
        } catch (errors) {
          handleError(errors, 'PlanItemActions.archivePlanItem')
        }
      }
    })
  }

  const deletePlanItem = () => {
    if (node.referenced || (get(node, 'entity.planItems.data', []) as unknown[]).length > 1) {
      return enqueueSnackbar('Record can not be deleted as it is referenced by another record!', {
        variant: 'warning',
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })
    }

    showModal({
      node,
      handleConfirm: async () => {
        try {
          let deleteMutation = deleteObjectiveMutation

          if (isAction) {
            deleteMutation = deleteActionMutation
          }
          if (isKPI) {
            deleteMutation = deleteKPIMutation
          }

          await deleteMutation({ variables: { id: node.entityId } })
          await treeQueryRefetch()
        } catch (errors) {
          handleError(errors, 'PlanItemActions.deletePlanItem')
        }
      }
    })
  }

  const handleRemoveClick = () => (shouldArchive ? archivePlanItem() : deletePlanItem())

  const unarchivePlanItem = async () => {
    try {
      await archiveMutation({ variables: { id: node.id, archived: false } })
      await treeQueryRefetch()
    } catch (errors) {
      handleError(errors, 'PlanItemActions.unarchivePlanItem')
    }
  }

  if (isLocked) {
    return null
  }

  // Archived rows: only unarchive + delete.
  if (node.archived) {
    if (!canArchive && !canDelete) {
      return null
    }

    return (
      <ButtonsWrapper>
        {canArchive && (
          <ActionButton description='Unarchive' onClick={unarchivePlanItem}>
            <UndoIcon />
          </ActionButton>
        )}
        {canDelete && (
          <ActionButton description='Delete' onClick={deletePlanItem}>
            <TrashIcon />
          </ActionButton>
        )}
      </ButtonsWrapper>
    )
  }

  return (
    <ButtonsWrapper>
      {canAdd && (
        <AddMenu
          node={node}
          levelOptions={levelOptions}
          nextLevelDescription={nextLevelDescription}
          shouldShowAddObjectiveButton={shouldShowAddObjectiveButton}
          shouldShowAddActionButton={shouldShowAddActionButton}
          shouldShowAddKPIButton={shouldShowAddKPIButton}
        />
      )}

      {isLinkingMode ? (
        <>
          {shouldShowLinkButton && (
            <ActionButton description='Link Item' onClick={linkToSource}>
              <AddLinkIcon />
            </ActionButton>
          )}
          {isNodeLinked && (
            <ActionButton description='Unlink Item' onClick={unlinkPlanItem}>
              <LinkOffIcon />
            </ActionButton>
          )}
        </>
      ) : (
        canLink && (
          <ActionButton description='Link Item' onClick={linkPlanItem}>
            <LinkIcon />
          </ActionButton>
        )
      )}

      {shouldShowRemoveButton && (
        <ActionButton description='Remove from Plan' onClick={removeReferencedPlanItem}>
          <UndoIcon />
        </ActionButton>
      )}

      {!isNodeReferenced && (canArchive || canDelete) && (
        <ActionButton description={shouldArchive ? 'Archive' : 'Delete'} onClick={handleRemoveClick}>
          {shouldArchive ? <ArchiveIcon /> : <TrashIcon />}
        </ActionButton>
      )}
    </ButtonsWrapper>
  )
}

export default PlanItemActions
